import {getDatasheetProperty, getManufacturerPartNumberProperty} from "@buildwithflux/core";
import type {IExtractedProperty, IPropertiesMap, IPropertyData, PropertySpecialState} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";
import {createSelector} from "reselect";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Derived selectors for document properties - computed values
 * These selectors perform filtering, mapping, or other computations on property data
 * Note: These use reselect for memoization of expensive computations
 */

/** Gets all property names - memoized */
export const names: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.properties],
    (properties): string[] => {
        if (!properties) return emptyArray;
        const result: string[] = [];
        for (const property of Records.iterate(properties)) {
            if (property.name) {
                result.push(property.name);
            }
        }
        return result;
    },
);

/** Gets a property by name - parameterized */
export const byName: ParameterizedSelector<string, IPropertiesMap[string] | undefined> = (name: string) => {
    return (state: DocumentStoreState): IPropertiesMap[string] | undefined => {
        const properties = state.properties;
        if (!properties) return undefined;
        return Records.findValue(properties, (property) => property.name === name);
    };
};

/** Gets the datasheet property from document properties */
export const datasheet: DocumentSelector<IPropertyData | IExtractedProperty | PropertySpecialState | undefined> = (
    state: DocumentStoreState,
) => {
    const properties = state.properties;
    if (!properties) return undefined;
    return getDatasheetProperty(properties);
};

/** Gets the manufacturer part number property from document properties */
export const manufacturerPartNumber: DocumentSelector<
    IPropertyData | IExtractedProperty | PropertySpecialState | undefined
> = (state: DocumentStoreState) => {
    const properties = state.properties;
    if (!properties) return undefined;
    return getManufacturerPartNumberProperty(properties);
};

/** Gets properties filtered by a predicate - parameterized */
export const filtered: ParameterizedSelector<
    (property: IPropertiesMap[string]) => boolean,
    Array<IPropertiesMap[string]>
> = (predicate: (property: IPropertiesMap[string]) => boolean) => {
    return (state: DocumentStoreState): Array<IPropertiesMap[string]> => {
        const properties = state.properties;
        if (!properties) return [];
        const result: Array<IPropertiesMap[string]> = [];
        for (const property of Records.iterate(properties)) {
            if (predicate(property)) {
                result.push(property);
            }
        }
        return result;
    };
};

/** Checks if a property exists by UID */
export const exists: ParameterizedSelector<string, boolean> = (uid: string) => {
    return (state: DocumentStoreState): boolean => {
        return state.properties?.[uid] !== undefined;
    };
};

/** Gets property UIDs that match a predicate - parameterized */
export const uidsByPredicate: ParameterizedSelector<(property: IPropertiesMap[string]) => boolean, string[]> = (
    predicate: (property: IPropertiesMap[string]) => boolean,
) => {
    return (state: DocumentStoreState): string[] => {
        const properties = state.properties;
        if (!properties) return emptyArray;
        const result: string[] = [];
        for (const [uid, property] of Object.entries(properties)) {
            if (property && predicate(property)) {
                result.push(uid);
            }
        }
        return result;
    };
};
